import { sdkUtils } from "@koala/sdk";
import { Component } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { StyledButtonContainer } from "../../accountInformation/styles";
import { OrderNowCTA } from "../orderNowCTA";
import {
  StyledFlexContainer,
  StyledLoyaltyStateContainer,
  StyledPointsHeader,
  StyledProgressBarHorizontal,
} from "./styles";
import StringAccessor from "@/components/cmsConfig/stringAccessor";
import {
  StyledMembershipLevelTag,
  StyledRewardDisclaimer,
} from "@/components/uielements/reward/styles";
import { PROGRAM_TYPES } from "@/constants/loyalty";
import { type RootState } from "@/types/app";
import { pluralizeString, safelyGetString } from "@/utils/stringHelpers";

class LoyaltyState extends Component<ReduxProps> {
  renderPointsHeader = (
    currentPoints: number,
    pointsUntilNextReward: number,
    nextPointTier: number
  ) => {
    const pointsCounter = pluralizeString(
      safelyGetString(this.props.strings, "rewards.rewards_counter"),
      nextPointTier > 1
    );
    // User has no points
    if (!currentPoints) {
      return (
        <StringAccessor accessor="rewards.no_rewards_header" html={true} />
      );
    }

    // User has reached max tier
    if (!pointsUntilNextReward) {
      return (
        <>
          <StringAccessor
            accessor="rewards.maximum_tier_header"
            html={true}
            dataObj={{ points: currentPoints.toLocaleString() }}
          />
        </>
      );
    }

    return (
      // All other cases
      <>
        {currentPoints.toLocaleString()}/{nextPointTier.toLocaleString()}{" "}
        {pointsCounter}
      </>
    );
  };

  render() {
    const { loyaltyState, organization, myRedeemables, strings } = this.props;

    const membershipLevelName = loyaltyState?.membership_level_name;
    const currentPoints = loyaltyState?.points ?? 0;
    const programType = organization?.loyalty.program_type;

    // Find the next largest reward after the one we can afford
    const nextPointTier = sdkUtils.nextLargestRewardPointValue(
      programType,
      // @ts-expect-error ensure that `loyaltyState` is defined.
      loyaltyState,
      myRedeemables
    );
    const progressBarWidth = sdkUtils.calculateProgressBarWidth(
      currentPoints,
      nextPointTier
    );
    const pointsUntilNextReward =
      nextPointTier - currentPoints > 0 ? nextPointTier - currentPoints : 0;
    const pointsCounter = pluralizeString(
      safelyGetString(strings, "rewards.rewards_counter"),
      pointsUntilNextReward !== 1
    );

    return (
      <StyledLoyaltyStateContainer>
        <div>
          {/* Main Points Header */}
          <StyledPointsHeader currentPoints={currentPoints}>
            {this.renderPointsHeader(
              currentPoints,
              pointsUntilNextReward,
              nextPointTier
            )}
          </StyledPointsHeader>

          {/* Progress Bar */}
          {pointsUntilNextReward > 0 && (
            <StyledProgressBarHorizontal>
              <div className="wrapper">
                <div className="progress-bar">
                  <span
                    className="progress-bar-fill"
                    style={{ width: progressBarWidth }}
                  />
                </div>
              </div>
            </StyledProgressBarHorizontal>
          )}

          {/* Points Subheader */}
          {pointsUntilNextReward > 0 ? (
            <>
              <h2>
                <StringAccessor
                  accessor="rewards.points_meter_subheader"
                  html={true}
                  dataObj={{
                    number: pointsUntilNextReward.toLocaleString(),
                    counter: pointsCounter,
                    conversionRate:
                      programType === PROGRAM_TYPES.POINTS_CONVERT_CURRENCY &&
                      `$${loyaltyState?.conversion_rate}`,
                  }}
                />
              </h2>
              <StringAccessor
                tag="h3"
                accessor="rewards.user_summary_total_available_points_text"
                html={true}
                dataObj={{ points: currentPoints.toString() }}
              />
            </>
          ) : (
            <StringAccessor
              tag="h2"
              accessor="rewards.maximum_tier_subheader"
              html={true}
            />
          )}

          {/* Order Now button */}
          {!pointsUntilNextReward && (
            <StyledFlexContainer>
              <StyledButtonContainer>
                <OrderNowCTA label="Rewards - Loyalty State" />
              </StyledButtonContainer>
            </StyledFlexContainer>
          )}

          {/* Member Level Tag */}
          {membershipLevelName && (
            <StyledMembershipLevelTag>
              <img src="/static/img/icons/trophy.svg" alt="Trophy Icon" />
              <StringAccessor
                accessor="rewards.user_summary_membership_level_text"
                html={true}
                dataObj={{ level: membershipLevelName }}
              />
            </StyledMembershipLevelTag>
          )}

          {/* Disclaimer */}
          <StringAccessor
            accessor="rewards.pending_points_disclaimer"
            html={true}
            tag={StyledRewardDisclaimer}
          />
        </div>
      </StyledLoyaltyStateContainer>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  loyaltyState: state.app.me.loyaltyState,
  myRedeemables: state.app.me.redeemables,
  organization: state.app.organization,
  strings: state.app.cmsConfig.strings,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(LoyaltyState);
